import React, { useEffect, useMemo, useState } from "react";

const GROUP_OPTIONS = [
  {
    id: "unchanged",
    label: "In both, unchanged",
    countKey: "unchanged",
    groupKey: "unchanged",
    mode: "records",
    dot: "bg-[var(--muted,#6b6a66)]",
    description: "No change",
    emptyHint: "No unchanged records.",
  },
  {
    id: "changed",
    label: "In both, with changes",
    countKey: "changed",
    groupKey: "changed",
    mode: "changed",
    dot: "bg-[#3b6ea8]",
    description: null,
    emptyHint: "No changed records.",
  },
  {
    id: "added",
    label: "New in file",
    countKey: "added",
    groupKey: "added",
    mode: "records",
    dot: "bg-[var(--good,#1f6b34)]",
    description: "Added to inventory",
    emptyHint: "No new records in this file.",
  },
  {
    id: "leaving",
    label: "Leaving inventory",
    countKey: "leaving",
    groupKey: "leaving",
    mode: "records",
    dot: "bg-[var(--accent,#a51c1c)]",
    description: "Nothing leaves inventory",
    emptyHint:
      "Nothing leaves inventory with this file. Every current circuit is also in the file.",
  },
  {
    id: "skipped",
    label: "Not imported from file",
    countKey: "skipped",
    groupKey: "skipped",
    mode: "skipped",
    dot: "bg-[var(--muted,#6b6a66)]",
    description: "Not counted in inventory",
    dashed: true,
    emptyHint: "Every row in this file will be imported.",
  },
];

const GROUP_BLURBS = {
  unchanged:
    "Same circuit, same values in inventory and in the file. Nothing changes for these.",
  changed:
    "Same circuit ID in inventory and the file, but one or more fields differ. Inventory will be updated to match the file.",
  added:
    "In the file but not in current inventory. These are added.",
  leaving:
    "In current inventory but not in the file. These are removed from inventory and kept in Import history.",
  skipped:
    "Rows in the file that will not be imported (rules, validation, duplicates, or excluded status).",
};

const PAGE_SIZE = 50;

const FIELD_LABELS = {
  mrc: "MRC",
  status: "Status",
  service_type: "Service type",
  billing_account: "Billing account",
  bandwidth: "Bandwidth",
  provider_circuit_id: "Provider circuit ID",
  last_mile_provider: "Last mile provider",
  contract_term: "Contract term",
  end_of_term: "End of term",
  street_address: "Address",
  city: "City",
  state: "State",
  zip: "ZIP",
  country: "Country",
  std_street_address: "Address",
  std_city: "City",
  std_state: "State",
  std_zip: "ZIP",
  std_country: "Country",
  site: "Address",
  notes: "Notes",
  action: "Action",
};

function formatMoney(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "$0";
  return n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
}

function parseMrc(value) {
  if (value == null || value === "") return 0;
  const n = Number(String(value).replace(/[^0-9.-]/g, ""));
  return Number.isFinite(n) ? n : 0;
}

function formatRecordAddress(record) {
  if (!record) return "—";
  if (record.site && String(record.site).trim()) {
    return String(record.site).trim();
  }
  const parts = [
    record.std_street_address || record.street_address,
    record.std_city || record.city,
    record.std_state || record.state,
    record.std_country || record.country,
    record.std_zip || record.zip,
  ].filter((p) => p != null && String(p).trim() !== "");
  return parts.length ? parts.join(", ") : "—";
}

function fieldLabel(field) {
  return FIELD_LABELS[field] || String(field || "").replace(/_/g, " ");
}

function getBeforeMrc(row) {
  const mrcChange = (row?.changes || []).find((c) => c.field === "mrc");
  if (mrcChange) return parseMrc(mrcChange.before);
  return parseMrc(row?.record?.mrc);
}

function getAfterMrc(row) {
  const mrcChange = (row?.changes || []).find((c) => c.field === "mrc");
  if (mrcChange) return parseMrc(mrcChange.after);
  return parseMrc(row?.record?.mrc);
}

function sumMrc(rows, picker) {
  return (Array.isArray(rows) ? rows : []).reduce(
    (sum, row) => sum + picker(row),
    0,
  );
}

function statusDotClass(status) {
  const s = String(status || "").toLowerCase();
  if (s.includes("service") || s === "active") return "bg-[var(--good,#1f6b34)]";
  if (s.includes("suspend")) return "bg-[#c47a12]";
  if (s.includes("cancel") || s.includes("disconnect")) {
    return "bg-[var(--accent,#a51c1c)]";
  }
  return "bg-[var(--muted,#6b6a66)]";
}

function compareSortValues(a, b, direction) {
  const dir = direction === "desc" ? -1 : 1;
  if (a == null && b == null) return 0;
  if (a == null || a === "") return 1;
  if (b == null || b === "") return -1;
  if (typeof a === "number" && typeof b === "number") {
    if (a === b) return 0;
    return (a < b ? -1 : 1) * dir;
  }
  return (
    String(a).localeCompare(String(b), undefined, {
      numeric: true,
      sensitivity: "base",
    }) * dir
  );
}

function usePagedRows(rows, page) {
  return useMemo(() => {
    const list = Array.isArray(rows) ? rows : [];
    const total = list.length;
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE) || 1);
    const safePage = Math.min(Math.max(1, page), totalPages);
    const start = (safePage - 1) * PAGE_SIZE;
    return {
      pageRows: list.slice(start, start + PAGE_SIZE),
      total,
      totalPages,
      safePage,
      start,
    };
  }, [rows, page]);
}

function PaginationBar({ page, totalPages, total, onPageChange }) {
  if (total <= PAGE_SIZE) return null;
  const start = (page - 1) * PAGE_SIZE + 1;
  const end = Math.min(page * PAGE_SIZE, total);
  return (
    <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-[12px] text-[var(--muted,#6b6a66)]">
      <span>
        Showing{" "}
        <span className="font-medium text-[var(--ink,#1c1c1a)]">{start}</span>
        {" – "}
        <span className="font-medium text-[var(--ink,#1c1c1a)]">{end}</span>
        {" of "}
        <span className="font-medium text-[var(--ink,#1c1c1a)]">{total}</span>
      </span>
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
          className="px-2.5 py-1 border border-[var(--line-strong,#d5d3cc)] rounded-md bg-white hover:bg-[var(--sunken,#eeece7)] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Prev
        </button>
        <span className="px-1">
          Page {page} of {totalPages}
        </span>
        <button
          type="button"
          disabled={page >= totalPages}
          onClick={() => onPageChange(page + 1)}
          className="px-2.5 py-1 border border-[var(--line-strong,#d5d3cc)] rounded-md bg-white hover:bg-[var(--sunken,#eeece7)] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Next
        </button>
      </div>
    </div>
  );
}

function SortHint({ active, direction }) {
  return (
    <span
      className="ml-1 inline-flex flex-col leading-none text-[9px] text-[var(--muted,#6b6a66)]"
      aria-hidden
    >
      <span
        className={
          active && direction === "asc"
            ? "text-[var(--ink,#1c1c1a)] opacity-100"
            : "opacity-60"
        }
      >
        ▲
      </span>
      <span
        className={`-mt-0.5 ${
          active && direction === "desc"
            ? "text-[var(--ink,#1c1c1a)] opacity-100"
            : "opacity-60"
        }`}
      >
        ▼
      </span>
    </span>
  );
}

function Th({ children, align = "left", sortKey, activeKey, direction, onSort }) {
  const active = Boolean(sortKey && sortKey === activeKey);
  return (
    <th
      scope="col"
      aria-sort={
        active ? (direction === "asc" ? "ascending" : "descending") : "none"
      }
      className={`py-2.5 px-3 text-[11px] font-semibold uppercase tracking-[0.04em] text-[var(--muted,#6b6a66)] ${
        align === "right" ? "text-right" : "text-left"
      } ${
        sortKey
          ? "cursor-pointer select-none hover:text-[var(--ink,#1c1c1a)]"
          : ""
      }`}
      onClick={sortKey ? () => onSort(sortKey) : undefined}
    >
      <span className="inline-flex items-center">
        {children}
        {sortKey ? <SortHint active={active} direction={direction} /> : null}
      </span>
    </th>
  );
}

function useColumnSort(rows, getSortValue) {
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  const sortedRows = useMemo(() => {
    const list = Array.isArray(rows) ? [...rows] : [];
    if (!sortKey) return list;
    list.sort((a, b) =>
      compareSortValues(getSortValue(a, sortKey), getSortValue(b, sortKey), sortDir),
    );
    return list;
  }, [rows, sortKey, sortDir, getSortValue]);

  const onSort = (key) => {
    if (sortKey === key) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  return { sortedRows, sortKey, sortDir, onSort };
}

function getRecordSortValue(row, key) {
  switch (key) {
    case "circuit_id":
      return row?.circuit_id || "";
    case "service_type":
      return row?.record?.service_type || "";
    case "status":
      return row?.record?.status || "";
    case "address":
      return formatRecordAddress(row?.record);
    case "mrc":
      return parseMrc(row?.record?.mrc);
    default:
      return "";
  }
}

function getSkippedSortValue(row, key) {
  switch (key) {
    case "row": {
      const n = Number(row?.row_number ?? row?.row);
      return Number.isFinite(n) ? n : Number.POSITIVE_INFINITY;
    }
    case "circuit_id":
      return row?.circuit_id || "";
    case "reason":
      return row?.reason || "";
    case "address":
      return formatRecordAddress(row?.record);
    case "mrc":
      return parseMrc(row?.record?.mrc);
    default:
      return "";
  }
}

function getChangedSortValue(row, key) {
  switch (key) {
    case "circuit_id":
      return row?.circuit_id || "";
    case "service_type":
      return row?.record?.service_type || "";
    case "what_changed":
      return (row?.changes || []).map((c) => fieldLabel(c.field)).join(", ");
    case "address":
      return formatRecordAddress(row?.record);
    case "current_mrc":
      return getBeforeMrc(row);
    case "file_mrc":
      return getAfterMrc(row);
    case "change":
      return getAfterMrc(row) - getBeforeMrc(row);
    default:
      return "";
  }
}

function RecordList({ rows, page, onPageChange, emptyHint }) {
  const { sortedRows, sortKey, sortDir, onSort } = useColumnSort(
    rows,
    getRecordSortValue,
  );
  const { pageRows, total, totalPages, safePage } = usePagedRows(sortedRows, page);

  useEffect(() => {
    if (safePage !== page) onPageChange(safePage);
  }, [safePage, page, onPageChange]);

  const handleSort = (key) => {
    onSort(key);
    if (page !== 1) onPageChange(1);
  };

  if (!rows?.length) {
    return (
      <p className="py-10 text-center text-[13px] text-[var(--muted,#6b6a66)]">
        {emptyHint || "No records in this group."}
      </p>
    );
  }

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="bg-[var(--sunken,#eeece7)] border-b border-[var(--line,#e4e2dc)]">
              <Th sortKey="circuit_id" activeKey={sortKey} direction={sortDir} onSort={handleSort}>
                Circuit ID
              </Th>
              <Th sortKey="service_type" activeKey={sortKey} direction={sortDir} onSort={handleSort}>
                Service type
              </Th>
              <Th sortKey="status" activeKey={sortKey} direction={sortDir} onSort={handleSort}>
                Status
              </Th>
              <Th sortKey="address" activeKey={sortKey} direction={sortDir} onSort={handleSort}>
                Address
              </Th>
              <Th
                align="right"
                sortKey="mrc"
                activeKey={sortKey}
                direction={sortDir}
                onSort={handleSort}
              >
                MRC
              </Th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map((r) => (
              <tr
                key={r.circuit_id}
                className="border-b border-[var(--line,#e4e2dc)] last:border-b-0"
              >
                <td className="py-2.5 px-3 font-medium text-[var(--accent,#a51c1c)]">
                  {r.circuit_id || "—"}
                </td>
                <td className="py-2.5 px-3 text-[var(--ink,#1c1c1a)]">
                  {r.record?.service_type || "—"}
                </td>
                <td className="py-2.5 px-3 text-[var(--ink,#1c1c1a)]">
                  <span className="inline-flex items-center gap-1.5">
                    <span
                      className={`w-1.5 h-1.5 rounded-full ${statusDotClass(
                        r.record?.status,
                      )}`}
                    />
                    {r.record?.status || "—"}
                  </span>
                </td>
                <td className="py-2.5 px-3 text-[var(--ink,#1c1c1a)]">
                  {formatRecordAddress(r.record)}
                </td>
                <td className="py-2.5 px-3 text-right tabular-nums text-[var(--ink,#1c1c1a)]">
                  {formatMoney(parseMrc(r.record?.mrc))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="px-3 pb-1 text-[12px] text-[var(--muted,#6b6a66)]">
        Showing {pageRows.length} of {total} records
      </div>
      <PaginationBar
        page={safePage}
        totalPages={totalPages}
        total={total}
        onPageChange={onPageChange}
      />
    </div>
  );
}

function SkippedList({ rows, page, onPageChange, emptyHint }) {
  const { sortedRows, sortKey, sortDir, onSort } = useColumnSort(
    rows,
    getSkippedSortValue,
  );
  const { pageRows, total, totalPages, safePage } = usePagedRows(sortedRows, page);

  useEffect(() => {
    if (safePage !== page) onPageChange(safePage);
  }, [safePage, page, onPageChange]);

  const handleSort = (key) => {
    onSort(key);
    if (page !== 1) onPageChange(1);
  };

  if (!rows?.length) {
    return (
      <p className="py-10 text-center text-[13px] text-[var(--muted,#6b6a66)]">
        {emptyHint || "Every row in this file will be imported."}
      </p>
    );
  }

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="bg-[var(--sunken,#eeece7)] border-b border-[var(--line,#e4e2dc)]">
              <Th sortKey="row" activeKey={sortKey} direction={sortDir} onSort={handleSort}>
                Row
              </Th>
              <Th sortKey="circuit_id" activeKey={sortKey} direction={sortDir} onSort={handleSort}>
                Circuit ID
              </Th>
              <Th sortKey="reason" activeKey={sortKey} direction={sortDir} onSort={handleSort}>
                Reason
              </Th>
              <Th sortKey="address" activeKey={sortKey} direction={sortDir} onSort={handleSort}>
                Address
              </Th>
              <Th
                align="right"
                sortKey="mrc"
                activeKey={sortKey}
                direction={sortDir}
                onSort={handleSort}
              >
                MRC in file
              </Th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map((r, index) => (
              <tr
                key={`${r.circuit_id || "no-circuit-id"}-${r.row_number ?? r.row ?? index}`}
                className="border-b border-[var(--line,#e4e2dc)] last:border-b-0"
              >
                <td className="py-2.5 px-3 text-[var(--muted,#6b6a66)] tabular-nums">
                  {r.row_number ?? r.row ?? (safePage - 1) * PAGE_SIZE + index + 1}
                </td>
                <td className="py-2.5 px-3 font-medium text-[var(--accent,#a51c1c)]">
                  {r.circuit_id || "—"}
                </td>
                <td className="py-2.5 px-3 text-[var(--ink,#1c1c1a)]">
                  {r.reason || "—"}
                </td>
                <td className="py-2.5 px-3 text-[var(--ink,#1c1c1a)]">
                  {formatRecordAddress(r.record)}
                </td>
                <td className="py-2.5 px-3 text-right tabular-nums text-[var(--ink,#1c1c1a)]">
                  {formatMoney(parseMrc(r.record?.mrc))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="px-3 pb-1 text-[12px] text-[var(--muted,#6b6a66)]">
        Showing {pageRows.length} of {total} rows
      </div>
      <PaginationBar
        page={safePage}
        totalPages={totalPages}
        total={total}
        onPageChange={onPageChange}
      />
    </div>
  );
}

function ChangedList({ rows, page, onPageChange, emptyHint }) {
  const { sortedRows, sortKey, sortDir, onSort } = useColumnSort(
    rows,
    getChangedSortValue,
  );
  const { pageRows, total, totalPages, safePage } = usePagedRows(sortedRows, page);

  useEffect(() => {
    if (safePage !== page) onPageChange(safePage);
  }, [safePage, page, onPageChange]);

  const handleSort = (key) => {
    onSort(key);
    if (page !== 1) onPageChange(1);
  };

  if (!rows?.length) {
    return (
      <p className="py-10 text-center text-[13px] text-[var(--muted,#6b6a66)]">
        {emptyHint || "No changed records."}
      </p>
    );
  }

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="bg-[var(--sunken,#eeece7)] border-b border-[var(--line,#e4e2dc)]">
              <Th sortKey="circuit_id" activeKey={sortKey} direction={sortDir} onSort={handleSort}>
                Circuit ID
              </Th>
              <Th sortKey="service_type" activeKey={sortKey} direction={sortDir} onSort={handleSort}>
                Service type
              </Th>
              <Th sortKey="what_changed" activeKey={sortKey} direction={sortDir} onSort={handleSort}>
                What changed
              </Th>
              <Th sortKey="address" activeKey={sortKey} direction={sortDir} onSort={handleSort}>
                Address
              </Th>
              <Th
                align="right"
                sortKey="current_mrc"
                activeKey={sortKey}
                direction={sortDir}
                onSort={handleSort}
              >
                Current MRC
              </Th>
              <Th
                align="right"
                sortKey="file_mrc"
                activeKey={sortKey}
                direction={sortDir}
                onSort={handleSort}
              >
                File MRC
              </Th>
              <Th
                align="right"
                sortKey="change"
                activeKey={sortKey}
                direction={sortDir}
                onSort={handleSort}
              >
                Change
              </Th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map((r) => {
              const before = getBeforeMrc(r);
              const after = getAfterMrc(r);
              const delta = after - before;
              const changeFields = (r.changes || []).map((c) =>
                fieldLabel(c.field),
              );
              return (
                <tr
                  key={r.circuit_id}
                  className="border-b border-[var(--line,#e4e2dc)] last:border-b-0"
                >
                  <td className="py-2.5 px-3 font-medium text-[var(--accent,#a51c1c)]">
                    {r.circuit_id || "—"}
                  </td>
                  <td className="py-2.5 px-3 text-[var(--ink,#1c1c1a)]">
                    {r.record?.service_type || "—"}
                  </td>
                  <td className="py-2.5 px-3">
                    <div className="flex flex-wrap gap-1">
                      {changeFields.length ? (
                        changeFields.map((label) => (
                          <span
                            key={label}
                            className="inline-flex rounded-md bg-[var(--sunken,#eeece7)] px-1.5 py-0.5 text-[11px] text-[var(--ink,#1c1c1a)]"
                          >
                            {label}
                          </span>
                        ))
                      ) : (
                        <span className="text-[var(--muted,#6b6a66)]">—</span>
                      )}
                    </div>
                  </td>
                  <td className="py-2.5 px-3 text-[var(--ink,#1c1c1a)]">
                    {formatRecordAddress(r.record)}
                  </td>
                  <td className="py-2.5 px-3 text-right tabular-nums text-[var(--ink,#1c1c1a)]">
                    {formatMoney(before)}
                  </td>
                  <td className="py-2.5 px-3 text-right tabular-nums text-[var(--ink,#1c1c1a)]">
                    {formatMoney(after)}
                  </td>
                  <td className="py-2.5 px-3 text-right tabular-nums">
                    {Math.abs(delta) < 0.005 ? (
                      <span className="text-[var(--muted,#6b6a66)]">no change</span>
                    ) : (
                      <span
                        className={
                          delta > 0
                            ? "text-[var(--accent,#a51c1c)]"
                            : "text-[var(--good,#1f6b34)]"
                        }
                      >
                        {delta > 0 ? "+" : ""}
                        {formatMoney(delta)}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="px-3 pb-1 text-[12px] text-[var(--muted,#6b6a66)]">
        Showing {pageRows.length} of {total} records
      </div>
      <PaginationBar
        page={safePage}
        totalPages={totalPages}
        total={total}
        onPageChange={onPageChange}
      />
    </div>
  );
}

function cardSubtitle(option, count, groups) {
  const rows = groups[option.groupKey] || [];
  if (option.id === "unchanged") {
    const mrc = sumMrc(rows, (r) => parseMrc(r.record?.mrc));
    return {
      line1: `${formatMoney(mrc)} MRC`,
      line2: count === 0 ? "No change" : option.description,
    };
  }
  if (option.id === "changed") {
    const before = sumMrc(rows, getBeforeMrc);
    const after = sumMrc(rows, getAfterMrc);
    const delta = after - before;
    const mrcChanged = rows.filter((r) =>
      (r.changes || []).some((c) => c.field === "mrc"),
    ).length;
    return {
      line1: `${formatMoney(before)} → ${formatMoney(after)}`,
      line2:
        count === 0
          ? "No changes"
          : `${delta >= 0 ? "+" : ""}${formatMoney(delta)} MRC${
              mrcChanged ? ` · ${mrcChanged} with an MRC change` : ""
            }`,
      line2Accent: delta !== 0,
    };
  }
  if (option.id === "added") {
    const mrc = sumMrc(rows, (r) => parseMrc(r.record?.mrc));
    return {
      line1: `${formatMoney(mrc)} MRC`,
      line2: count === 0 ? "None added" : option.description,
    };
  }
  if (option.id === "leaving") {
    const mrc = sumMrc(rows, (r) => parseMrc(r.record?.mrc));
    return {
      line1: `${formatMoney(mrc)} MRC`,
      line2: count === 0 ? "Nothing leaves inventory" : "Removed from inventory",
    };
  }
  if (option.id === "skipped") {
    const mrc = sumMrc(rows, (r) => parseMrc(r.record?.mrc));
    return {
      line1: `${formatMoney(mrc)} in file`,
      line2: option.description,
    };
  }
  return { line1: "", line2: "" };
}

/**
 * Record-group cards + detail table for replace-preview comparison.
 * Defaults to the first group ("In both, unchanged").
 */
export default function ReplaceComparisonGroupFilter({ comparison }) {
  const counts = comparison?.counts || {};
  const groups = comparison?.groups || {};
  const [selected, setSelected] = useState(GROUP_OPTIONS[0].id);
  const [page, setPage] = useState(1);

  const activeOption = GROUP_OPTIONS.find((o) => o.id === selected) || null;
  const rows = activeOption ? groups[activeOption.groupKey] || [] : [];
  const activeCount = activeOption
    ? counts[activeOption.countKey] ?? rows.length ?? 0
    : 0;

  const fileRows =
    comparison?.file_row_count ?? comparison?.file_count ?? 0;
  const willImport = comparison?.file_count ?? 0;
  const notImported = counts.skipped ?? 0;

  const handleSelect = (id) => {
    setSelected(id);
    setPage(1);
  };

  return (
    <div className="mt-5 space-y-4">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div className="text-[11px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)]">
          Record groups
        </div>
        <div className="text-[12px] text-[var(--muted,#6b6a66)]">
          {Number(fileRows).toLocaleString()} rows in the file ·{" "}
          {Number(willImport).toLocaleString()} will import ·{" "}
          {Number(notImported).toLocaleString()} not imported
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        {GROUP_OPTIONS.map((option) => {
          const count = counts[option.countKey] ?? 0;
          const isSelected = selected === option.id;
          const isEmpty = count === 0;
          const sub = cardSubtitle(option, count, groups);
          return (
            <button
              key={option.id}
              type="button"
              onClick={() => handleSelect(option.id)}
              className={`text-left rounded-[10px] bg-white px-3.5 py-3 transition-colors ${
                option.dashed && !isSelected
                  ? "border border-dashed border-[var(--line-strong,#d5d3cc)]"
                  : "border"
              } ${
                isSelected
                  ? "border-[var(--accent,#a51c1c)] shadow-sm"
                  : option.dashed
                    ? ""
                    : "border-[var(--line,#e4e2dc)] hover:border-[var(--line-strong,#d5d3cc)]"
              } ${isEmpty && !isSelected ? "opacity-70" : ""}`}
            >
              <div className="flex items-center gap-1.5 text-[12px] text-[var(--ink,#1c1c1a)]">
                <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${option.dot}`} />
                <span className="truncate font-medium">{option.label}</span>
              </div>
              <div
                className={`mt-2 text-[26px] font-semibold leading-none tabular-nums ${
                  isEmpty
                    ? "text-[var(--muted,#6b6a66)]"
                    : "text-[var(--ink,#1c1c1a)]"
                }`}
              >
                {Number(count).toLocaleString()}
              </div>
              <div className="mt-1.5 text-[12px] text-[var(--muted,#6b6a66)] truncate">
                {sub.line1}
              </div>
              <div
                className={`mt-0.5 text-[11px] truncate ${
                  sub.line2Accent
                    ? "text-[var(--accent,#a51c1c)]"
                    : "text-[var(--muted,#6b6a66)]"
                }`}
              >
                {sub.line2}
              </div>
            </button>
          );
        })}
      </div>

      <div className="rounded-[10px] border border-[var(--line,#e4e2dc)] bg-white overflow-hidden">
        {!activeOption ? (
          <p className="px-4 py-8 text-sm text-[var(--muted,#6b6a66)] text-center">
            Select a record group above to view its records.
          </p>
        ) : (
          <>
            <div className="px-4 pt-4 pb-3 flex items-start justify-between gap-3 flex-wrap border-b border-[var(--line,#e4e2dc)]">
              <div className="min-w-0">
                <h3 className="text-[16px] font-semibold text-[var(--ink,#1c1c1a)]">
                  {activeOption.label}{" "}
                  <span className="font-semibold text-[var(--ink,#1c1c1a)]">
                    ({Number(activeCount).toLocaleString()})
                  </span>
                </h3>
                <p className="mt-1 text-[13px] text-[var(--muted,#6b6a66)] max-w-3xl">
                  {GROUP_BLURBS[activeOption.id]}
                </p>
              </div>
            </div>
            <div className="px-1 pt-1 pb-3">
              {activeOption.mode === "changed" ? (
                <ChangedList
                  key={activeOption.id}
                  rows={rows}
                  page={page}
                  onPageChange={setPage}
                  emptyHint={activeOption.emptyHint}
                />
              ) : activeOption.mode === "skipped" ? (
                <SkippedList
                  key={activeOption.id}
                  rows={rows}
                  page={page}
                  onPageChange={setPage}
                  emptyHint={activeOption.emptyHint}
                />
              ) : (
                <RecordList
                  key={activeOption.id}
                  rows={rows}
                  page={page}
                  onPageChange={setPage}
                  emptyHint={activeOption.emptyHint}
                />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
